import { create } from "zustand";
import {
  getAllLeaves,
  getLeaveById,
  approveLeave,
  rejectLeave,
  getPendingLeaves,
  getUpcomingLeaves,
  getDeclinedLeaves,
  getWithdrawnLeaves,
  getLeaveTypes,
  getLeaveBalancesSummary,
  getAvailableFinancialYears,
  correctLeaveDates as correctLeaveDatesService,
  updateLeaveQuota as updateLeaveQuotaService
} from "@/services/HrService/LeavesService";

const useLeavesStore = create((set, get) => ({
  leaves: null,
  pendingLeaves: null,
  upcomingLeaves: null,
  declinedLeaves: null,
  withdrawnLeaves: null,
  leaveTypes: null,
  selectedLeave: null,
  leaveBalance: null,
  allEmployeeBalances: null,
  leaveBalancesSummary: null,
  loading: false,
  error: null,
  // Financial year state
  availableFinancialYears: [],
  selectedFinancialYear: null,
  carryForwardPending: false,  // ✅ New flag
  // Pagination state for each tab
  allLeavesPagination: null,
  pendingLeavesPagination: null,
  upcomingLeavesPagination: null,
  declinedLeavesPagination: null,
  withdrawnLeavesPagination: null,
  leaveBalancesPagination: null,
  currentPageAllLeaves: 1,
  currentPagePending: 1,
  currentPageUpcoming: 1,
  currentPageDeclined: 1,
  currentPageWithdrawn: 1,
  currentPageBalances: 1,
  pageSize: 10,

  // Fetch all leaves
  fetchAllLeaves: async (page, limit, fyYear) => {
    try {
      set({ loading: true, error: null });
      const { currentPageAllLeaves, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPageAllLeaves;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;

      const response = await getAllLeaves(pageToFetch, limitToFetch, fyYearToFetch);
      console.log("All Leaves API Response:", response);

      if (response.success) {
        set({
          leaves: response.data,
          allLeavesPagination: response.pagination,
          currentPageAllLeaves: pageToFetch,
          loading: false
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch leaves', loading: false });
      }
    } catch (error) {
      console.error("Fetch All Leaves Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch pending leaves
  fetchPendingLeaves: async (page, limit, fyYear) => {
    try {
      const { currentPagePending, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPagePending;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;

      const response = await getPendingLeaves(pageToFetch, limitToFetch, fyYearToFetch);
      console.log("Pending Leaves API Response:", response);

      if (response.success) {
        set({
          pendingLeaves: response.data,
          pendingLeavesPagination: response.pagination,
          currentPagePending: pageToFetch
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch pending leaves' });
      }
    } catch (error) {
      console.error("Fetch Pending Leaves Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Set page for all leaves tab
  setPageAllLeaves: async (page) => {
    await get().fetchAllLeaves(page);
  },

  // Set page for pending leaves tab
  setPagePending: async (page) => {
    await get().fetchPendingLeaves(page);
  },

  // Fetch upcoming leaves
  fetchUpcomingLeaves: async (page, limit, fyYear) => {
    try {
      const { currentPageUpcoming, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPageUpcoming;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;

      const response = await getUpcomingLeaves(pageToFetch, limitToFetch, fyYearToFetch);
      console.log("Upcoming Leaves API Response:", response);

      if (response.success) {
        set({
          upcomingLeaves: response.data,
          upcomingLeavesPagination: response.pagination,
          currentPageUpcoming: pageToFetch
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch upcoming leaves' });
      }
    } catch (error) {
      console.error("Fetch Upcoming Leaves Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Set page for upcoming leaves tab
  setPageUpcoming: async (page) => {
    await get().fetchUpcomingLeaves(page);
  },

  // Fetch declined leaves
  fetchDeclinedLeaves: async (page, limit, fyYear) => {
    try {
      const { currentPageDeclined, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPageDeclined;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;

      const response = await getDeclinedLeaves(pageToFetch, limitToFetch, fyYearToFetch);
      console.log("Declined Leaves API Response:", response);

      if (response.success) {
        set({
          declinedLeaves: response.data,
          declinedLeavesPagination: response.pagination,
          currentPageDeclined: pageToFetch
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch declined leaves' });
      }
    } catch (error) {
      console.error("Fetch Declined Leaves Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Set page for declined leaves tab
  setPageDeclined: async (page) => {
    await get().fetchDeclinedLeaves(page);
  },

  // Fetch withdrawn leaves
  fetchWithdrawnLeaves: async (page, limit, fyYear) => {
    try {
      const { currentPageWithdrawn, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPageWithdrawn;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;

      const response = await getWithdrawnLeaves(pageToFetch, limitToFetch, fyYearToFetch);
      console.log("Withdrawn Leaves API Response:", response);

      if (response.success) {
        set({
          withdrawnLeaves: response.data,
          withdrawnLeavesPagination: response.pagination,
          currentPageWithdrawn: pageToFetch
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch withdrawn leaves' });
      }
    } catch (error) {
      console.error("Fetch Withdrawn Leaves Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Set page for withdrawn leaves tab
  setPageWithdrawn: async (page) => {
    await get().fetchWithdrawnLeaves(page);
  },


  // Fetch leave types
  fetchLeaveTypes: async () => {
    try {
      const response = await getLeaveTypes();
      console.log("Leave Types API Response:", response);
      
      if (response.success) {
        set({ leaveTypes: response.data });
      } else {
        set({ error: response.error?.message || 'Failed to fetch leave types' });
      }
    } catch (error) {
      console.error("Fetch Leave Types Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Fetch single leave
  fetchLeaveById: async (id) => {
    try {
      set({ loading: true, error: null });
      const response = await getLeaveById(id);
      console.log("Leave Details API Response:", response);
      
      if (response.success) {
        set({ selectedLeave: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch leave details', loading: false });
      }
    } catch (error) {
      console.error("Fetch Leave Details Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Approve leave
  approveLeaveRequest: async (id, approvedBy, remarks = null) => {
    try {
      set({ loading: true, error: null });
      const response = await approveLeave(id, approvedBy, remarks);
      console.log("Approve Leave API Response:", response);

      if (response.success) {
        set({ loading: false });
        // Refresh all data to get updated leave from server
        get().fetchAllLeaveData();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to approve leave', loading: false });
        throw new Error(response.error?.message || 'Failed to approve leave');
      }
    } catch (error) {
      console.error("Approve Leave Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Reject leave
  rejectLeaveRequest: async (id, approvedBy, remarks = null) => {
    try {
      set({ loading: true, error: null });
      const response = await rejectLeave(id, approvedBy, remarks);
      console.log("Reject Leave API Response:", response);

      if (response.success) {
        set({ loading: false });
        // Refresh all data to get updated leave from server
        get().fetchAllLeaveData();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to reject leave', loading: false });
        throw new Error(response.error?.message || 'Failed to reject leave');
      }
    } catch (error) {
      console.error("Reject Leave Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },


  // Fetch all leave data
  fetchAllLeaveData: async () => {
    try {
      set({ loading: true, error: null });

      await Promise.all([
        get().fetchAllLeaves(),
        get().fetchPendingLeaves(),
        get().fetchUpcomingLeaves(),
        get().fetchDeclinedLeaves(),
        get().fetchWithdrawnLeaves(),
        get().fetchLeaveTypes()
      ]);

      set({ loading: false });
    } catch (error) {
      console.error("Fetch All Leave Data Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Clear selected leave
  clearSelectedLeave: () => {
    set({ selectedLeave: null });
  },


  // Fetch available financial years
  fetchAvailableFinancialYears: async () => {
    try {
      const response = await getAvailableFinancialYears();
      console.log("Available Financial Years API Response:", response);

      if (response.success) {
        const { selectedFinancialYear } = get();
        set({ availableFinancialYears: response.data });

        // Auto-select current FY if nothing selected yet
        if (!selectedFinancialYear && response.currentFY) {
          set({ selectedFinancialYear: response.currentFY });
        }
      } else {
        set({ error: response.error?.message || 'Failed to fetch financial years' });
      }
    } catch (error) {
      console.error("Fetch Available Financial Years Error:", error);
      set({ error: error.message });
      throw error;
    }
  },

  // Set selected financial year
  setSelectedFinancialYear: async (fyYear) => {
    set({ selectedFinancialYear: fyYear });
    // Refresh ALL leave data with the selected year
    await Promise.all([
      get().fetchAllLeaves(1, null, fyYear),
      get().fetchPendingLeaves(1, null, fyYear),
      get().fetchUpcomingLeaves(1, null, fyYear),
      get().fetchDeclinedLeaves(1, null, fyYear),
      get().fetchWithdrawnLeaves(1, null, fyYear),
      get().fetchLeaveBalancesSummary(1, null, fyYear)
    ]);
  },

  // Fetch leave balances summary
  fetchLeaveBalancesSummary: async (page, limit, fyYear, search) => {
    try {
      set({ loading: true, error: null });
      const { currentPageBalances, pageSize, selectedFinancialYear } = get();
      const pageToFetch = page || currentPageBalances;
      const limitToFetch = limit || pageSize;
      const fyYearToFetch = fyYear !== undefined ? fyYear : selectedFinancialYear;
      const searchTerm = search !== undefined ? search : null;

      const response = await getLeaveBalancesSummary(pageToFetch, limitToFetch, fyYearToFetch, searchTerm);
      console.log("Leave Balances Summary API Response:", response);

      if (response.success) {
        set({
          leaveBalancesSummary: response.data,
          leaveBalancesPagination: response.pagination,
          currentPageBalances: pageToFetch,
          carryForwardPending: response.carryForwardPending || false,  // ✅ Save flag
          loading: false
        });
        // Update selectedFinancialYear if returned from API
        if (response.selectedFY) {
          set({ selectedFinancialYear: response.selectedFY });
        }
      } else {
        set({ error: response.error?.message || 'Failed to fetch leave balances summary', loading: false });
      }
    } catch (error) {
      console.error("Fetch Leave Balances Summary Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Set page for leave balances tab
  setPageBalances: async (page) => {
    await get().fetchLeaveBalancesSummary(page);
  },

  // Correct leave dates
  correctLeaveDates: async (id, newStartDate, newEndDate) => {
    try {
      set({ loading: true, error: null });
      const response = await correctLeaveDatesService(id, newStartDate, newEndDate);
      console.log("Correct Leave Dates API Response:", response);

      if (response.success) {
        set({ loading: false });
        get().fetchAllLeaveData();
        get().fetchLeaveBalancesSummary();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to correct leave dates', loading: false });
        throw new Error(response.error?.message || 'Failed to correct leave dates');
      }
    } catch (error) {
      console.error("Correct Leave Dates Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Update leave quota manually
  updateLeaveQuota: async (userId, leaveTypeId, leavesTaken, totalLeaves = null) => {
    try {
      set({ loading: true, error: null });
      const response = await updateLeaveQuotaService(userId, leaveTypeId, leavesTaken, totalLeaves);
      console.log("Update Leave Quota API Response:", response);

      if (response.success) {
        set({ loading: false });
        get().fetchLeaveBalancesSummary();
        return response.data;
      } else {
        set({ error: response.error?.message || 'Failed to update leave quota', loading: false });
        throw new Error(response.error?.message || 'Failed to update leave quota');
      }
    } catch (error) {
      console.error("Update Leave Quota Error:", error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Reset errors
  resetErrors: () => set({ error: null }),
}));

export default useLeavesStore;